/**
 * Setup CSP nonce for all style injections
 * 
 * Patches:
 * - JSS (used by @mui/styles)
 * - Webpack style-loader (for SCSS/CSS files)
 * - Emotion (handled separately via CacheProvider)
 * - Any other style tag creation
 */

export const setupJssNonce = (nonce) => {
  if (!nonce || typeof document === 'undefined') {
    return;
  }

  // Method 1: Patch document.createElement to add nonce to ALL style elements
  // This must happen BEFORE webpack style-loader or JSS creates any styles
  if (!document.createElement._noncePatched) {
    const originalCreateElement = document.createElement.bind(document);
    document.createElement = function(tagName, options) {
      const element = originalCreateElement(tagName, options);
      if (tagName && tagName.toLowerCase() === 'style') {
        // Set nonce immediately when style element is created
        // This catches webpack style-loader, JSS, and any other style injections
        element.setAttribute('nonce', nonce);
        try {
          // Set as property (some browsers use this)
          Object.defineProperty(element, 'nonce', {
            value: nonce,
            writable: true,
            configurable: true,
          });
        } catch (e) {
          // Fallback if property can't be set
        }
      }
      return element;
    };
    document.createElement._noncePatched = true;
  }

  // Also patch insertBefore and appendChild to catch style tags added via these methods
  // (webpack style-loader might use these)
  if (!document.head.appendChild._noncePatched) {
    const originalAppendChild = document.head.appendChild.bind(document.head);
    document.head.appendChild = function(child) {
      if (child && child.nodeName === 'STYLE' && !child.getAttribute('nonce')) {
        child.setAttribute('nonce', nonce);
      }
      return originalAppendChild(child);
    };
    document.head.appendChild._noncePatched = true;
  }

  if (!document.head.insertBefore._noncePatched) {
    const originalInsertBefore = document.head.insertBefore.bind(document.head);
    document.head.insertBefore = function(newNode, referenceNode) {
      if (newNode && newNode.nodeName === 'STYLE' && !newNode.getAttribute('nonce')) {
        newNode.setAttribute('nonce', nonce);
      }
      return originalInsertBefore(newNode, referenceNode);
    };
    document.head.insertBefore._noncePatched = true;
  }

  // Method 2: Use MutationObserver to catch ALL style tags (including webpack style-loader)
  // This is critical for webpack's style-loader which injects SCSS/CSS
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1 && node.nodeName === 'STYLE') {
          // Add nonce to ALL style tags immediately
          if (!node.getAttribute('nonce')) {
            node.setAttribute('nonce', nonce);
            try {
              Object.defineProperty(node, 'nonce', {
                value: nonce,
                writable: true,
                configurable: true,
              });
            } catch (e) {
              // Ignore if property can't be set
            }
          }
        }
      });
    });
  });

  // Observe document head (where most style tags are injected)
  observer.observe(document.head, {
    childList: true,
    subtree: true,
    attributes: false,
  });

  // Also observe document body (some loaders inject there)
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: false,
  });

  // Observe document element as well (catch-all)
  if (document.documentElement) {
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: false,
    });
  }

  // Method 3: Patch ALL existing style tags (including webpack style-loader tags)
  const updateExistingStyles = () => {
    const allStyles = document.querySelectorAll('style');
    let patched = 0;
    allStyles.forEach((style) => {
      if (!style.getAttribute('nonce')) {
        style.setAttribute('nonce', nonce);
        try {
          Object.defineProperty(style, 'nonce', {
            value: nonce,
            writable: true,
            configurable: true,
          });
        } catch (e) {
          // Ignore if property can't be set
        }
        patched++;
      }
    });
  };

  // Update immediately
  updateExistingStyles();

  // Update very frequently to catch webpack style-loader injections
  // style-loader can inject styles at any time during module loading
  const interval = setInterval(updateExistingStyles, 10);
  
  // Keep running for longer to catch lazy-loaded styles and HMR updates
  setTimeout(() => {
    clearInterval(interval);
    // Final check
    updateExistingStyles();
  }, 60000); // Run for 60 seconds to catch HMR updates

  return () => {
    observer.disconnect();
    clearInterval(interval);
    // Note: We don't restore createElement to avoid breaking other code
    // The patch should remain active for the lifetime of the app
  };
};

